// ! 国际化修改组件
import React, { useRef, useState } from 'react';
import { connect, useIntl, useModel } from 'umi';
import { cloneDeep, debounce, filter } from 'lodash';
import ProTable from '@ant-design/pro-table';
import { Button, Input, message, Modal, Tooltip } from 'antd';
import type { FormInstance } from '@ant-design/pro-form';
import { ModalForm, ProFormList, ProFormText } from '@ant-design/pro-components';
import { GlobalOutlined, QuestionCircleOutlined, UndoOutlined } from '@ant-design/icons';

import type { LocationState } from '@/models/locationState';
import { modifyLanguageList } from '@/components/RightContent/service';
import type { LayoutContentWrapProps } from '@/components/LayoutContentWrap';
import styles from './index.less';
import { withModalRootContainer } from '@/providers/AntdProvider';

type LocaleModifyCompProps = {
  initIntlSucceed?: boolean;
  setInitIntlSucceed?: (s: boolean) => void;
  updateIntlMessages?: () => void;
}
const LocaleModifyComp: React.FC<LayoutContentWrapProps & LocaleModifyCompProps> = ({
  dispatch,
  initIntlSucceed,
  setInitIntlSucceed,
  updateIntlMessages,
}) => {
  // 国际化
  const intl = useIntl();
  const c = window.$tc('', intl.formatMessage);
  const t = window.$tc('components.LocaleModifyComp.index', intl.formatMessage);

  const { currentTabIndex } = useModel('headerContentTabList');
  const formRef = useRef<FormInstance>();
  const [locales, setLocales] = useState<Record<string, any>[]>([]);
  const [visible, setVisible] = useState(false);

  return (
    <>
      <Button
        type="text"
        icon={<GlobalOutlined />}
        onClick={async () => {
          if (!initIntlSucceed) {
            await updateIntlMessages?.();
            dispatch({ type: 'locationState/reloadPage', payload: currentTabIndex });
            setInitIntlSucceed?.(true);
          }
          const messages = window.localeList.sort((a, b) => a > b ? 1 : -1).map(id => ({
            id,
            defaultMessage: intl.messages[id],
            isModified: false,
          }));
          setLocales(messages.map(item => ({ ...item })));
          setVisible(true);
          setTimeout(() => {
            formRef.current?.setFieldsValue({ messages });
          }, 0);
        }}
      />

      <ModalForm
        formRef={formRef}
        title={t('t0', '修改国际化配置')}
        width={600}
        open={visible}
        onOpenChange={setVisible}
        layout="horizontal"
        onFinish={async ({ messages }: { messages: Record<string, any>[] }) => {
          const modifiedList = filter(messages, item => {
            const record = locales.find(r => r.id === item.id);
            return item.defaultMessage !== record?.defaultMessage;
          });
          // const modifiedObj = modifiedList.reduce((obj, { id, defaultMessage }) => {
          //   obj[id] = defaultMessage;
          //   return obj;
          // }, {});
          // console.log(modifiedObj);
          // intl.messages = { ...intl.messages, ...modifiedObj };
          console.log(modifiedList);
          if (modifiedList.length) {
            withModalRootContainer(Modal.confirm)({
              title: t('t1', '确认修改吗？'),
              width: 800,
              content: (
                <ProTable
                  rowKey="id"
                  search={false}
                  pagination={false}
                  toolBarRender={false}
                  scroll={{ y: '60vh' }}
                  columns={[
                    { title: t('t2', '序号'), dataIndex: 'index', width: 50 },
                    { title: t('t3', '修改前'), dataIndex: 'oldMessage' },
                    { title: t('t4', '修改后'), dataIndex: 'defaultMessage' },
                  ]}
                  dataSource={modifiedList.map((item, index) => ({
                    ...item,
                    index: index + 1,
                    oldMessage: intl.messages[item.id],
                  }))}
                />
              ),
              onOk: async () => {
                modifiedList.forEach(({ id, defaultMessage }) => {
                  intl.messages[id] = defaultMessage;
                  const localeItem = locales.find(item => item.id === id);
                  if (localeItem) {
                    localeItem.defaultMessage = defaultMessage;
                  }
                });
                const { code, msg } = await modifyLanguageList(modifiedList.map(({ id, defaultMessage }) => ({ code: id, value: defaultMessage })));
                if (code === 200) {
                  dispatch({ type: 'locationState/reloadPage', payload: currentTabIndex });
                  setVisible(false);
                } else {
                  message.error(msg);
                }
              },
            });
            return false;
          } else {
            return true;
          }
        }}
        modalProps={{
          bodyStyle: {
            maxHeight: '70vh',
            overflowY: 'auto'
          }
        }}
      >
        <Input.Search
          style={{ marginBottom: 24 }}
          allowClear
          enterButton
          placeholder={t('t6', '输入关键词查找')}
          onSearch={val => {
            const messages = window.localeList.sort((a, b) => a > b ? 1 : -1).map(id => ({
              id,
              defaultMessage: intl.messages[id] as string,
              isModified: false,
            })).filter(item => item.defaultMessage.includes(val));
            setLocales(messages.map(item => ({ ...item })));
            setTimeout(() => {
              formRef.current?.setFieldsValue({ messages });
            }, 0);
          }}
        />
        <ProFormList
          // @ts-ignore
          className={styles.formList}
          name="messages"
          copyIconProps={false}
          deleteIconProps={false}
          creatorButtonProps={false}
        >
          {(meta, index) => (
            <div>
              <ProFormText name="id" hidden />
              <ProFormText
                name="defaultMessage"
                label={null}
                rules={[{
                  validator: (rule, value) => {
                    if (value == '') {
                      return Promise.reject(t('t5', '输入框不能为空！'));
                    }
                    return Promise.resolve();
                  }
                }]}
                fieldProps={{
                  onChange: debounce(e => {
                    locales[index].isModified = e.target.value !== locales[index].defaultMessage;
                    setLocales(cloneDeep(locales));
                  }, 500),
                  addonBefore: (
                    <Tooltip title={locales?.[index]?.id}>
                      <QuestionCircleOutlined style={{ cursor: 'pointer' }} />
                    </Tooltip>
                  ),
                  addonAfter: locales?.[index]?.isModified
                    ? <UndoOutlined onClick={() => {
                        locales[index].isModified = false;
                        const { messages } = formRef.current?.getFieldsValue();
                        messages[index] = locales[index];
                        formRef.current?.setFieldsValue({ messages: cloneDeep(messages) });
                        formRef.current?.validateFields();
                        setLocales(cloneDeep(locales));
                      }} />
                    : null,
                }}
              />
            </div>
          )}
        </ProFormList>
      </ModalForm>
    </>
  );
};

// export default LocaleModifyComp;
export const mapToLocationStateProps = ({ locationState }: { locationState: LocationState }) => {
  return {
    locationState
  }
}
export default connect(mapToLocationStateProps)(LocaleModifyComp);
